import React from "react";
import { createEmptyCareContext, normalizeCareContext } from "../../../utils/careContext";
import { createEmptySupportProfile } from "../../../utils/supportProfile";

export const createEmptyChildProfileFields = () => ({
  name: "",
  age: "",
  dateOfBirth: "",
  photo: null,
  photoURL: null,
  selectedConditions: [],
  foodAllergies: [],
  dietaryRestrictions: [],
  sensoryIssues: [],
  behavioralTriggers: [],
  communicationNeeds: [],
  careContext: createEmptyCareContext(),
  supportProfile: createEmptySupportProfile(),
});

export const normalizeChildProfileFields = (fields = {}) => {
  const emptyFields = createEmptyChildProfileFields();

  return {
    ...emptyFields,
    ...fields,
    selectedConditions: Array.isArray(fields.selectedConditions)
      ? fields.selectedConditions
      : emptyFields.selectedConditions,
    foodAllergies: Array.isArray(fields.foodAllergies)
      ? fields.foodAllergies
      : emptyFields.foodAllergies,
    dietaryRestrictions: Array.isArray(fields.dietaryRestrictions)
      ? fields.dietaryRestrictions
      : emptyFields.dietaryRestrictions,
    sensoryIssues: Array.isArray(fields.sensoryIssues)
      ? fields.sensoryIssues
      : emptyFields.sensoryIssues,
    behavioralTriggers: Array.isArray(fields.behavioralTriggers)
      ? fields.behavioralTriggers
      : emptyFields.behavioralTriggers,
    communicationNeeds: Array.isArray(fields.communicationNeeds)
      ? fields.communicationNeeds
      : emptyFields.communicationNeeds,
    careContext: normalizeCareContext(fields.careContext || emptyFields.careContext),
    supportProfile: fields.supportProfile || emptyFields.supportProfile,
  };
};

export const useChildProfileFields = (initialFields = {}) => {
  const normalizedInitialFields = normalizeChildProfileFields(initialFields);
  const [name, setName] = React.useState(normalizedInitialFields.name);
  const [age, setAge] = React.useState(normalizedInitialFields.age);
  const [dateOfBirth, setDateOfBirth] = React.useState(normalizedInitialFields.dateOfBirth);
  const [photo, setPhoto] = React.useState(normalizedInitialFields.photo);
  const [photoURL, setPhotoURL] = React.useState(normalizedInitialFields.photoURL);
  const [selectedConditions, setSelectedConditions] = React.useState(normalizedInitialFields.selectedConditions);
  const [foodAllergies, setFoodAllergies] = React.useState(normalizedInitialFields.foodAllergies);
  const [dietaryRestrictions, setDietaryRestrictions] = React.useState(normalizedInitialFields.dietaryRestrictions);
  const [sensoryIssues, setSensoryIssues] = React.useState(normalizedInitialFields.sensoryIssues);
  const [behavioralTriggers, setBehavioralTriggers] = React.useState(normalizedInitialFields.behavioralTriggers);
  const [communicationNeeds, setCommunicationNeeds] = React.useState(normalizedInitialFields.communicationNeeds);
  const [careContext, setCareContext] = React.useState(normalizedInitialFields.careContext);
  const [supportProfile, setSupportProfile] = React.useState(normalizedInitialFields.supportProfile);

  const setProfileFields = React.useCallback((nextFields = {}) => {
    const fields = normalizeChildProfileFields(nextFields);

    setName(fields.name);
    setAge(fields.age);
    setDateOfBirth(fields.dateOfBirth);
    setPhoto(fields.photo);
    setPhotoURL(fields.photoURL);
    setSelectedConditions(fields.selectedConditions);
    setFoodAllergies(fields.foodAllergies);
    setDietaryRestrictions(fields.dietaryRestrictions);
    setSensoryIssues(fields.sensoryIssues);
    setBehavioralTriggers(fields.behavioralTriggers);
    setCommunicationNeeds(fields.communicationNeeds);
    setCareContext(fields.careContext);
    setSupportProfile(fields.supportProfile);
  }, []);

  const resetProfileFields = React.useCallback(() => {
    setProfileFields();
  }, [setProfileFields]);

  return {
    name,
    setName,
    age,
    setAge,
    dateOfBirth,
    setDateOfBirth,
    photo,
    setPhoto,
    photoURL,
    setPhotoURL,
    selectedConditions,
    setSelectedConditions,
    foodAllergies,
    setFoodAllergies,
    dietaryRestrictions,
    setDietaryRestrictions,
    sensoryIssues,
    setSensoryIssues,
    behavioralTriggers,
    setBehavioralTriggers,
    communicationNeeds,
    setCommunicationNeeds,
    careContext,
    setCareContext,
    supportProfile,
    setSupportProfile,
    setProfileFields,
    resetProfileFields,
  };
};
